/**
 * Calculates the monthly mortgage payment using the standard amortization formula
 * @param loanAmount - The principal loan amount
 * @param annualInterestRate - The annual interest rate as a percentage (e.g., 5.5 for 5.5%)
 * @param loanTermMonths - The loan term in months (e.g., 360 for 30 years)
 * @returns The monthly payment amount
 */
export const calculateMonthlyPayment = (
  loanAmount: number,
  annualInterestRate: number,
  loanTermMonths: number
): number => {
  // Convert annual interest rate to monthly rate (as a decimal)
  const monthlyRate = annualInterestRate / 100 / 12;

  // Calculate monthly payment using the standard amortization formula
  const monthlyPayment =
    (loanAmount * monthlyRate * Math.pow(1 + monthlyRate, loanTermMonths)) /
    (Math.pow(1 + monthlyRate, loanTermMonths) - 1);

  return monthlyPayment;
};

/**
 * Calculates the total monthly payment including mortgage insurance
 * @param loanAmount - The principal loan amount
 * @param annualInterestRate - The annual interest rate as a percentage
 * @param loanTermMonths - The loan term in months
 * @param mortgageInsurance - The monthly mortgage insurance amount
 * @returns The total monthly payment including mortgage insurance
 */
export const calculateTotalMonthlyPayment = (
  loanAmount: number,
  annualInterestRate: number,
  loanTermMonths: number,
  mortgageInsurance: number = 0
): number => {
  const basePayment = calculateMonthlyPayment(
    loanAmount,
    annualInterestRate,
    loanTermMonths
  );

  return basePayment + mortgageInsurance;
};

/**
 * Calculates the loan amount based on purchase price and down payment
 * @param purchasePrice - The total purchase price
 * @param downPayment - The down payment amount
 * @returns The loan amount
 */
export const calculateLoanAmount = (
  purchasePrice: number,
  downPayment: number
): number => {
  return purchasePrice - downPayment;
};
